// src/components/PaiementsManagement.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, addDoc, onSnapshot, query, orderBy, serverTimestamp, deleteDoc, doc, setDoc, getDoc, getDocs, updateDoc, where } from 'firebase/firestore';
import { format, startOfDay, endOfDay, isWithinInterval, startOfMonth, endOfMonth, isSameDay, isAfter, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MessageSquare, DollarSign, Loader2, Filter, Printer, Menu, X, ChevronDown } from 'lucide-react';
import { getAuth } from 'firebase/auth'; 

/**
 * Composant de gestion des paiements pour les rôles administratifs (admin, financier, superAdmin).
 *
 * @param {object} props - Les propriétés du composant.
 * @param {object} props.db - Instance de Firebase Firestore.
 * @param {function} props.setError - Fonction pour afficher une erreur.
 * @param {function} props.setMessage - Fonction pour afficher un message de succès.
 * @param {string} props.schoolId - ID de l'école sélectionnée.
 * @param {string} props.userRole - Rôle de l'utilisateur ('financier', 'admin', 'superAdmin').
 */
const PaiementsManagement = ({ db, setError, setMessage, schoolId, userRole, eleveId }) => {
  const auth = getAuth(); 

  const [paiements, setPaiements] = useState([]);
  const [eleves, setEleves] = useState([]);
  const [classes, setClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // États pour le formulaire d'enregistrement de paiement
  const [selectedEleveId, setSelectedEleveId] = useState('');
  const [montantPaye, setMontantPaye] = useState('');
  const [moisConcerne, setMoisConcerne] = useState(''); // Format YYYY-MM
  const [typePaiement, setTypePaiement] = useState('mensualite');
  const [description, setDescription] = useState('');
  const [moyenPaiement, setMoyenPaiement] = useState('');
  const [referenceTransaction, setReferenceTransaction] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [currentPaiementId, setCurrentPaiementId] = useState(null);

  // États pour les filtres
  const [filterEleveId, setFilterEleveId] = useState('');
  const [filterClasseId, setFilterClasseId] = useState('');
  const [filterTypePaiement, setFilterTypePaiement] = useState('');
  const [filterMoisAnnee, setFilterMoisAnnee] = useState('');
  const [filterSoldeNonAJour, setFilterSoldeNonAJour] = useState(false);
  const [filterMoyenPaiement, setFilterMoyenPaiement] = useState('');
  const [showFilter, setShowFilter] = useState(false); 

  // --- NOUVEAUX ÉTATS ---
  const [selectedSchoolYear, setSelectedSchoolYear] = useState('');
  const [activeView, setActiveView] = useState('paiementsHistory'); // 'paiementsHistory' ou 'studentStatus'
  const [filterCasSocialOnly, setFilterCasSocialOnly] = useState(false); // Filtre pour la vue Statut Élèves
  const [showBulkReminderPanel, setShowBulkReminderPanel] = useState(false); // NOUVEAU: Panneau de rappel groupé
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Menu mobile pour les tabs

  // États pour la gestion de caisse
  const [totalCashInToday, setTotalCashInToday] = useState(0);
  const [totalCashInMonth, setTotalCashInMonth] = useState(0);

  // État pour les données de l'école (reçu)
  const [schoolData, setSchoolData] = useState(null);

  // Références pour stocker les données brutes Firestore
  const elevesRawRef = useRef([]);
  const enrollmentsRef = useRef([]);
  const classesRef = useRef([]);
  const paiementsRef = useRef([]);
  
  // États de chargement individuels
  const [loadingElevesData, setLoadingElevesData] = useState(true);
  const [loadingEnrollmentsData, setLoadingEnrollmentsData] = useState(true);
  const [loadingClassesData, setLoadingClassesData] = useState(true);
  const [loadingPaiementsData, setLoadingPaiementsData] = useState(true);

  // --- NOUVELLE FONCTION : updateStudentReceivable ---
  const updateStudentReceivable = async (studentId, schoolId, currentSoldeDu, eleveName) => {
    if (!db || !schoolId || !studentId) return;

    const creanceDocRef = doc(db, 'schools', schoolId, 'creances_clients', studentId);
    try {
      if (currentSoldeDu > 0) {
        await setDoc(creanceDocRef, {
          eleveId: studentId,
          schoolId: schoolId,
          eleveName: eleveName,
          montantDu: currentSoldeDu,
          lastUpdated: serverTimestamp(),
        }, { merge: true });
      } else {
        const docSnap = await getDoc(creanceDocRef);
        if (docSnap.exists()) {
          await deleteDoc(creanceDocRef);
        }
      }
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la créance client pour ${eleveName}:`, error);
      setError(`Erreur lors de la mise à jour de la créance client pour ${eleveName}.`);
    }
  };

  const fetchSchoolInfo = async () => {
    if (schoolId && db) {
      try {
        const schoolRef = doc(db, 'schools', schoolId);
        const docSnap = await getDoc(schoolRef);
        if (docSnap.exists()) {
          setSchoolData(docSnap.data());
        } else {
          console.error("Aucun document d'école trouvé pour l'ID:", schoolId);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des informations de l'école:", error);
        setError("Erreur lors du chargement des informations de l'école.");
      }
    }
  };

  // Génère les 3 dernières années scolaires
  const recentSchoolYears = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    let baseYear = currentMonth >= 8 ? now.getFullYear() : now.getFullYear() - 1;
    const years = [];
    for (let i = 0; i < 3; i++) {
      const start = baseYear - i;
      years.push(`${start}-${start + 1}`);
    }
    return years;
  }, []);

  // Initialiser selectedSchoolYear à l'année courante
  useEffect(() => {
    if (!selectedSchoolYear && recentSchoolYears.length > 0) {
      setSelectedSchoolYear(recentSchoolYears[0]);
    }
  }, [selectedSchoolYear, recentSchoolYears]);
  
  // Fonction utilitaire pour formater la date
 // Fonction utilitaire pour formater la date - SIMPLIFIÉE
const formatDateForDisplay = (date) => {
  if (!date) return 'Date inconnue';
  
  try {
    // Vérifier le type et convertir
    const d = date instanceof Date ? date : 
             (date && date.toDate && typeof date.toDate === 'function') ? 
             date.toDate() : new Date(date);
    
    // Vérifier la validité
    if (isNaN(d.getTime())) {
      return 'Date invalide';
    }
    
    return format(d, 'dd/MM/yyyy HH:mm', { locale: fr });
  } catch (error) {
    console.error('Erreur de formatage:', date, error);
    return 'Date erreur';
  }
};

  // Fonction utilitaire pour formater les montants en devise (XOF)
  const formatCurrency = (amount) => {
    if (typeof amount !== 'number' || isNaN(amount)) return '0 XOF';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Mettre à jour l'état de chargement global
  useEffect(() => {
    setIsLoading(loadingElevesData || loadingEnrollmentsData || loadingClassesData || loadingPaiementsData);
  }, [loadingElevesData, loadingEnrollmentsData, loadingClassesData, loadingPaiementsData]);

  // Fonction pour combiner les données brutes des élèves avec les inscriptions
  const combineAndSetElevesState = useCallback((elevesRaw, enrollmentsRaw) => {
    if (!elevesRaw || elevesRaw.length === 0) {
      setEleves([]);
      return;
    }

    const latestEnrollments = enrollmentsRaw.reduce((acc, enrollment) => {
      const studentId = enrollment.studentId;
      const currentTimestamp = enrollment.updatedAt; 
      const existingTimestamp = acc[studentId]?.updatedAt;

      const isNewer = existingTimestamp
        ? (currentTimestamp?.seconds > existingTimestamp?.seconds || (currentTimestamp?.seconds === existingTimestamp?.seconds && currentTimestamp?.nanoseconds > existingTimestamp?.nanoseconds))
        : true;
          
      if (!acc[studentId] || isNewer) {
        acc[studentId] = enrollment;
      }
      return acc;
    }, {});
      
    const enrolledStudentIds = new Set(Object.keys(latestEnrollments));

    const combinedElevesData = elevesRaw
      .filter(rawEleve => enrolledStudentIds.has(rawEleve.id))
      .map(eleve => {
          const enrollment = latestEnrollments[eleve.id];
          const classInfoForEleve = classesRef.current.find(c => c.id === enrollment.classId);

          const combined = {
              ...eleve,
              classeId: enrollment ? enrollment.classId : null,
              className: classInfoForEleve ? classInfoForEleve.nom : (enrollment ? enrollment.className : null),
              schoolYear: enrollment ? enrollment.schoolYear : null,
              boursierResponsibleName: enrollment ? enrollment.boursierResponsibleName : null,
              isCasSocial: enrollment?.isCasSocial || false, 
              casSocialFeeType: enrollment?.casSocialFeeType || null, 
              casSocialFeeValue: enrollment?.casSocialFeeValue || 0, 
              telephoneParent: eleve.guardianPhone || eleve.telephoneParent || null
          };
          return combined;
      });
          
    setEleves(combinedElevesData);
  }, [setEleves, classesRef]);

  // --- CHARGEMENT DES DONNÉES FIREBASE (USEEFFECT) ---
  useEffect(() => {
    if (!db || !schoolId) {
      if (!schoolId) {
        setMessage("Veuillez sélectionner une école pour gérer les paiements.");
      }
      setLoadingElevesData(false);
      setLoadingEnrollmentsData(false);
      setLoadingClassesData(false);
      setLoadingPaiementsData(false);
      return;
    }
    setError('');
    setMessage('');

    fetchSchoolInfo(); 

    const unsubscribes = [];

    // 1. Récupération des élèves
    setLoadingElevesData(true);
    const elevesCollectionRef = collection(db, 'schools', schoolId, 'eleves');
    const unsubscribeEleves = onSnapshot(elevesCollectionRef, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      elevesRawRef.current = data;
      setLoadingElevesData(false);
    }, (error) => {
      setError(`Erreur lors du chargement des données brutes des élèves: ${error.message}`);
      setLoadingElevesData(false);
    });
    unsubscribes.push(unsubscribeEleves);

    // 2. Récupération des classes
    setLoadingClassesData(true);
    const classesCollectionRef = collection(db, 'schools', schoolId, 'classes');
    const unsubscribeClasses = onSnapshot(classesCollectionRef, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      classesRef.current = data;
      setClasses(data);
      setLoadingClassesData(false);
    }, (error) => {
      setError(`Erreur lors du chargement des classes: ${error.message}`);
      setLoadingClassesData(false);
    });
    unsubscribes.push(unsubscribeClasses);

    // 3. Récupération des inscriptions
    setLoadingEnrollmentsData(true);
    const enrollmentsCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
    const qEnrollments = query(enrollmentsCollectionRef, where('schoolYear', '==', selectedSchoolYear));
    const unsubscribeEnrollments = onSnapshot(qEnrollments, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      enrollmentsRef.current = data;
      setLoadingEnrollmentsData(false);
    }, (error) => {
      setError(`Erreur lors du chargement des inscriptions: ${error.message}`);
      setLoadingEnrollmentsData(false);
    });
    unsubscribes.push(unsubscribeEnrollments);

    // 4. Récupération des paiements
    setLoadingPaiementsData(true);
    const paiementsCollectionRef = collection(db, 'schools', schoolId, 'paiements');
    const qPaiements = query(paiementsCollectionRef, orderBy("datePaiement", "desc"));
    const unsubscribePaiements = onSnapshot(qPaiements, (snapshot) => {
      const paiementsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      paiementsRef.current = paiementsData;
      setPaiements(paiementsData);
      
      const now = new Date();
      const startOfCurrentDay = startOfDay(now);
      const endOfCurrentDay = now;
      const startOfCurrentMonth = startOfMonth(now);
      const endOfCurrentMonth = endOfMonth(now);
    
      let dailyTotal = 0;
      let monthlyTotal = 0;
      paiementsData.forEach(p => {
        if (p.datePaiement && p.datePaiement.toDate) {
          const paiementDate = p.datePaiement.toDate();
          const montant = parseFloat(p.montantPaye) || 0;
          if (p.moyenPaiement === 'Espèces' && isWithinInterval(paiementDate, { start: startOfCurrentDay, end: endOfCurrentDay })) {
            dailyTotal += montant;
          }
          if (p.moyenPaiement === 'Espèces' && isWithinInterval(paiementDate, { start: startOfCurrentMonth, end: endOfCurrentMonth })) {
            monthlyTotal += montant;
          }
        }
      });
      setTotalCashInToday(dailyTotal);
      setTotalCashInMonth(monthlyTotal);
      setLoadingPaiementsData(false);
    }, (error) => {
      setError(`Erreur lors du chargement des paiements: ${error.message}`);
      setLoadingPaiementsData(false);
    });
    unsubscribes.push(unsubscribePaiements);

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [db, setError, setMessage, schoolId, selectedSchoolYear]);

  // Nouvel useEffect pour combiner les données des élèves et des inscriptions
  useEffect(() => {
    if (!loadingElevesData && !loadingEnrollmentsData && elevesRawRef.current && enrollmentsRef.current) {
        combineAndSetElevesState(elevesRawRef.current, enrollmentsRef.current);
    }
  }, [loadingElevesData, loadingEnrollmentsData, elevesRawRef, enrollmentsRef, combineAndSetElevesState]);

  // Fonction pour récupérer les infos de classe d'un élève
  const getEleveClasseInfo = (eleveId) => {
  const eleve = eleves.find(e => e.id === eleveId);
  if (!eleve) {
    return { 
      eleveNomComplet: 'Élève inconnu', 
      classeNom: 'N/A', 
      classeInfo: null, 
      telephoneParent: '' // Retourner string vide au lieu de null
    };
  }
  
  // Assurer que telephoneParent est toujours une string
  const telephoneParent = String(eleve.guardianPhone || eleve.telephoneParent || '');
  
  const classe = eleve.classeId ? classes.find(c => c.id === eleve.classeId) : null;
  return {
    eleveNomComplet: eleve.name,
    classeNom: classe ? classe.nom : (eleve.className || 'N/A'),
    classeInfo: classe,
    telephoneParent: telephoneParent
  };
};

  // --- Fonction de génération du lien WhatsApp ---
 // --- Fonction de génération du lien WhatsApp ---
const generateWhatsAppLink = (eleveNomComplet, soldeDu, telephoneParent) => {
  // Vérifier que telephoneParent existe et est une string
  if (!telephoneParent || typeof telephoneParent !== 'string') {
    console.warn('Numéro de téléphone invalide pour WhatsApp:', telephoneParent);
    return null;
  }
  
  let cleanPhoneNumber = telephoneParent.replace(/[^0-9]/g, '');
  
  // Vérifier si le numéro a au moins quelques chiffres
  if (cleanPhoneNumber.length < 7) {
    console.warn('Numéro de téléphone trop court:', cleanPhoneNumber);
    return null;
  }
  
  // Normaliser le numéro
  if (cleanPhoneNumber.startsWith('00')) { 
    cleanPhoneNumber = cleanPhoneNumber.substring(2); 
  }
  if (!cleanPhoneNumber.startsWith('221')) { 
    cleanPhoneNumber = '221' + cleanPhoneNumber;
  }
  
  const message = `Bonjour, ceci est un rappel concernant le paiement des frais de scolarité de votre enfant ${eleveNomComplet}.
Le solde dû est de ${soldeDu.toFixed(0)} XOF. Merci de bien vouloir régulariser la situation.`;
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanPhoneNumber}?text=${encodedMessage}`;
};
  // --- LOGIQUE FINANCIÈRE DE BASE ---
  const getStudentFinancialDetails = (eleve, allPaiements) => {
    const defaultResult = {
        totalAnnualFee: 0,
        totalExpectedDue: 0,
        totalPaid: 0,
        soldeAnnuelDu: 0,
        soldeDuAjourdhui: 0,
        monthlyStatuses: [],
        statusSummary: 'N/A',
    };

    if (!eleve || !eleve.schoolYear) return defaultResult;
    const { classeInfo } = getEleveClasseInfo(eleve.id);
    if (!classeInfo) return { ...defaultResult, statusSummary: 'Classe non définie' };

    const montantInscriptionStandard = parseFloat(classeInfo.montantInscription) || 0;
    const montantMensualiteStandard = parseFloat(classeInfo.montantMensualite) || 0;
    const totalStandardAnnualFee = montantInscriptionStandard + (montantMensualiteStandard * 9); 

    const [startYearStr, endYearStr] = eleve.schoolYear.split('-');
    const startYear = parseInt(startYearStr, 10);
    const endYear = parseInt(endYearStr, 10);
    const currentDate = new Date();

    let totalAnnualFee = totalStandardAnnualFee; 
    let totalExpectedDue = 0; 
    let totalPaid = allPaiements.filter(p => p.eleveId === eleve.id)
        .reduce((sum, p) => sum + (parseFloat(p.montantPaye) || 0), 0);

    const monthlyPaymentSchedule = [
        { month: 10, year: startYear, name: 'Nov' }, 
        { month: 11, year: startYear, name: 'Dec' }, 
        { month: 0, year: endYear, name: 'Jan' },   
        { month: 1, year: endYear, name: 'Feb' },   
        { month: 2, year: endYear, name: 'Mar' },   
        { month: 3, year: endYear, name: 'Apr' },   
        { month: 4, year: endYear, name: 'May' },   
        { month: 5, year: endYear, name: 'Jun' },   
        { month: 6, year: endYear, name: 'Jul' },   
    ];

    let monthlyStatuses = [];
    let paiementsRestants = totalPaid; 

    // --- 1. CAS SOCIAL: CALCUL DES FRAIS ANNUELS SPÉCIAUX ---
    if (eleve.isCasSocial) {
        if (eleve.casSocialFeeType === 'annual') {
            totalAnnualFee = parseFloat(eleve.casSocialFeeValue) || 0;
        } else if (eleve.casSocialFeeType === 'percentage') {
            const reductionPercentage = parseFloat(eleve.casSocialFeeValue) || 0;
            totalAnnualFee = totalStandardAnnualFee * ((100 - reductionPercentage) / 100);
        }
        
        totalExpectedDue = totalAnnualFee; 
        
        const annualSolde = totalAnnualFee - totalPaid;
        const paidRatio = totalPaid / totalAnnualFee;
        monthlyStatuses.push({
            monthName: 'Annuel Cas Social',
            expectedDue: totalAnnualFee,
            paid: totalPaid,
            solde: annualSolde,
            status: paidRatio >= 1 ? 'PAYÉ' : (paidRatio > 0 ? 'PARTIEL' : 'IMPAYÉ'),
        });
        
    } 
    // --- 2. TARIF STANDARD: CALCUL DÉTAILLÉ PAR POSTE ---
    else {
        const inscriptionDueDate = new Date(startYear, 9, 1);
        let inscriptionExpected = montantInscriptionStandard;
        
        if (isAfter(currentDate, inscriptionDueDate) || isSameDay(currentDate, inscriptionDueDate)) {
            totalExpectedDue += inscriptionExpected;
        }

        let inscriptionPaid = Math.min(paiementsRestants, inscriptionExpected);
        paiementsRestants -= inscriptionPaid;

        monthlyStatuses.push({
            monthName: 'Inscription',
            expectedDue: montantInscriptionStandard,
            paid: inscriptionPaid,
            solde: montantInscriptionStandard - inscriptionPaid,
            status: inscriptionPaid >= montantInscriptionStandard ? 'PAYÉ' : (inscriptionPaid > 0 ? 'PARTIEL' : 'IMPAYÉ'),
        });
        
        monthlyPaymentSchedule.forEach(monthItem => {
            const monthlyDueDate = new Date(monthItem.year, monthItem.month, 1);
            let monthlyExpected = montantMensualiteStandard;
            
            if (isAfter(currentDate, monthlyDueDate) || isSameDay(currentDate, monthlyDueDate)) {
                totalExpectedDue += monthlyExpected;
            }

            let monthlyPaid = Math.min(paiementsRestants, monthlyExpected);
            paiementsRestants -= monthlyPaid;
            
            monthlyStatuses.push({
                monthName: format(monthlyDueDate, 'MMM yyyy', { locale: fr }),
                expectedDue: monthlyExpected,
                paid: monthlyPaid,
                solde: monthlyExpected - monthlyPaid,
                status: monthlyPaid >= monthlyExpected ? 'PAYÉ' : (monthlyPaid > 0 ? 'PARTIEL' : 'IMPAYÉ'),
            });
        });
    }

    const soldeAnnuelDu = totalAnnualFee - totalPaid;
    const soldeDuAjourdhui = Math.max(0, totalExpectedDue - totalPaid); 
    
    let statusSummary = 'À JOUR';
    if (soldeDuAjourdhui > 0) {
        statusSummary = 'EN RETARD';
    } else if (soldeAnnuelDu > 0 && soldeDuAjourdhui === 0) {
        statusSummary = 'AVANCE/RESTANT À PAYER';
    } else if (soldeAnnuelDu <= 0) {
        statusSummary = 'SOLDÉ';
    }

    return {
        ...defaultResult,
        totalAnnualFee,
        totalExpectedDue,
        totalPaid,
        soldeAnnuelDu,
        soldeDuAjourdhui,
        monthlyStatuses,
        statusSummary,
    };
  };

  // --- MISE À JOUR : calculateSolde ---
  const calculateSolde = (eleveId) => {
    const eleve = eleves.find(e => e.id === eleveId);
    if (!eleve || !eleve.schoolYear) {
      return "N/A (données élève/année scolaire manquantes)";
    }
    const details = getStudentFinancialDetails(eleve, paiementsRef.current); 
    return details.soldeDuAjourdhui;
  };
  
  // --- Liste de statut des élèves (pour la nouvelle vue) ---
  const elevesStatusList = useMemo(() => {
      const currentEleves = eleves.filter(e => e.schoolYear === selectedSchoolYear);

      return currentEleves.map(eleve => {
          const details = getStudentFinancialDetails(eleve, paiementsRef.current);
          return {
              ...eleve,
              ...details,
          };
      }).filter(status => {
          if (filterCasSocialOnly && !status.isCasSocial) return false;
          return true;
      });
  }, [eleves, paiementsRef.current, selectedSchoolYear, filterCasSocialOnly]);
  
  // --- NOUVEAU : Statistiques de Recouvrement ---
  const recoveryStats = useMemo(() => {
    let totalAnnualExpected = 0;
    let totalPaid = 0;
    let totalExpectedDueToDate = 0;

    elevesStatusList.forEach(status => {
        if (status.totalAnnualFee > 0) {
            totalAnnualExpected += status.totalAnnualFee;
            totalPaid += status.totalPaid;
            totalExpectedDueToDate += status.totalExpectedDue;
        }
    });

    const annualRecoveryRate = totalAnnualExpected > 0 
        ? (totalPaid / totalAnnualExpected) * 100 
        : 0;
    
    const dueDateRecoveryRate = totalExpectedDueToDate > 0
        ? (totalPaid / totalExpectedDueToDate) * 100 
        : 0;

    const normalizedDueDateRecoveryRate = Math.min(100, dueDateRecoveryRate); 
    
    return {
        totalAnnualExpected,
        totalPaid,
        totalExpectedDueToDate,
        annualRecoveryRate,
        dueDateRecoveryRate: normalizedDueDateRecoveryRate,
    };
}, [elevesStatusList]);

  // --- NOUVEAU : Liste des élèves en retard (pour le rappel groupé) ---
  const overdueStudents = useMemo(() => {
    return elevesStatusList
      .filter(status => status.soldeDuAjourdhui > 0)
      .map(status => ({
        id: status.id,
        name: status.name,
        soldeDu: status.soldeDuAjourdhui,
        telephoneParent: status.telephoneParent,
        whatsAppLink: status.soldeDuAjourdhui > 0 && status.telephoneParent 
            ? generateWhatsAppLink(status.name, status.soldeDuAjourdhui, status.telephoneParent)
            : null,
      }));
  }, [elevesStatusList]);
  
  // Logique de filtrage des paiements côté client
  const filteredPaiements = useMemo(() => {
    const elevesWithOutstandingBalanceIds = new Set();
    if (filterSoldeNonAJour) {
      eleves.forEach(eleve => {
        const solde = calculateSolde(eleve.id);
        if (typeof solde === 'number' && solde > 0) {
          elevesWithOutstandingBalanceIds.add(eleve.id);
        }
      });
    }

    return paiements.filter(paiement => {
      const eleve = eleves.find(e => e.id === paiement.eleveId);
      if (!eleve) return false;
      if (filterEleveId && paiement.eleveId !== filterEleveId) return false;
      if (filterClasseId && eleve.classeId !== filterClasseId) return false;
      if (filterTypePaiement && paiement.typePaiement !== filterTypePaiement) return false;
      if (filterMoyenPaiement && paiement.moyenPaiement !== filterMoyenPaiement) return false;
      if (filterMoisAnnee && paiement.moisConcerne !== filterMoisAnnee) return false;
     
      if (filterSoldeNonAJour && !elevesWithOutstandingBalanceIds.has(paiement.eleveId)) return false;
      if (eleve.schoolYear !== selectedSchoolYear) return false; 
      
      return true;
    });
  }, [
    paiements, eleves, filterEleveId, filterClasseId, filterTypePaiement,
    filterMoisAnnee, filterSoldeNonAJour, filterMoyenPaiement, selectedSchoolYear
  ]);

  // Fonction pour rendre un tableau responsive avec défilement horizontal
  const ResponsiveTable = ({ children, className = "" }) => {
    return (
      <div className={`overflow-x-auto -mx-4 sm:mx-0 ${className}`}>
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            {children}
          </div>
        </div>
      </div>
    );
  };

  // --- Fonction d'impression du reçu ---
  const handlePrintPaymentReceipt = (paiement, newPaiementId) => {
    if (!schoolData) {
      setError("Informations de l'école non chargées. Impossible d'imprimer.");
      return;
    }
    const receiptId = newPaiementId || paiement.id;
    // Dans handlePrintPaymentReceipt, ligne ~545
const dateToFormat = paiement.datePaiement ?
  (paiement.datePaiement.toDate ? paiement.datePaiement.toDate() : new Date(paiement.datePaiement)) : 
  new Date();
    const formattedDate = formatDateForDisplay(dateToFormat);
    const formattedAmount = formatCurrency(paiement.montant || paiement.montantPaye);
    const studentName = eleves.find(e => e.id === paiement.eleveId)?.name || 'Élève Inconnu';
    const schoolName = schoolData?.name || 'Nom de l\'École';
    const schoolAddress = schoolData?.adresse_ecole || 'Adresse de l\'École';
    const schoolPhone = schoolData?.telephone_ecole || 'Téléphone de l\'École';
    const designation = paiement.description || (paiement.typePaiement === 'mensualite' ? `Mensualité (${paiement.moisConcerne})` : paiement.typePaiement) || 'Frais de Scolarité';
    const schoolLogoHtml = schoolData?.logoUrl
      ?
      `<img src="${schoolData.logoUrl}" style="max-height: 60px; width: auto; margin: 0 auto 10px; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';"
      alt="Logo"/>
         <h3 style="display: none; margin: 2px 0; font-size: 11pt;">${schoolName}</h3>`
      : `<h3>${schoolName}</h3>`;
    const content = `
      <html>
        <head>
          <title>Reçu #${receiptId.substring(0, 8)}</title>
          <style>
            @page { size: 80mm auto;
              margin: 0; }
            body {
              font-family: 'Consolas', monospace;
              font-size: 10pt;
              width: 80mm;
              padding: 5mm;
              margin: 0;
              color: #000;
              box-sizing: border-box;
            }
            .ticket {
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
              padding: 10px 0;
            }
            .header { margin-bottom: 10px;
              padding-bottom: 10px; border-bottom: 1px dashed #ccc; width: 100%; }
            .details { width: 100%;
              text-align: left; margin-bottom: 15px; }
            .details p { margin: 3px 0;
              display: flex; justify-content: space-between; }
            .details strong { font-weight: bold;
            }
            .line-item { margin: 5px 0; width: 100%;
              display: flex; justify-content: space-between; }
            .total {
              font-size: 13pt;
              font-weight: bold;
              margin-top: 15px;
              padding-top: 10px;
              border-top: 2px solid #000;
              width: 100%;
              text-align: right;
            }
            .footer {
                margin-top: 20px;
              padding-top: 10px;
                border-top: 1px dashed #ccc;
                width: 100%;
            }
            .footer p { margin: 2px 0;
              font-size: 9pt; }
            @media print {
              .ticket { padding: 0;
              }
              body { font-size: 9pt; width: 80mm;
              padding: 0 5mm; }
              .header { border-bottom: 1px dashed #000;
              }
              .footer { border-top: 1px dashed #000;
              padding-top: 10px; }
              .total { font-size: 12pt;
              }
            }
          </style>
        </head>
        <body>
          <div class="ticket">
            <div class="header">
              ${schoolLogoHtml}
              <p>${schoolAddress}</p>
          
              <p>Tél: ${schoolPhone}</p>
            </div>
            <h1 style="font-size: 14pt; margin: 10px 0;">REÇU DE PAIEMENT</h1>
            <div class="details">
                <p><strong>Reçu N° :</strong> <span>${receiptId.substring(0, 8).toUpperCase()}</span></p>
                <p><strong>Date/Heure :</strong> <span>${formattedDate}</span></p>
             
                <p><strong>Payé par :</strong> <span>${studentName}</span></p>
                <p><strong>ID Élève :</strong> <span>${paiement.eleveId.substring(0, 8)}...</span></p>
            </div>
            <div style="width: 100%; padding-top: 5px; border-top: 1px dashed #ccc;">
                <div class="line-item">
                    <span class="label">Désignation:</span>
    
                    <span class="value">${designation}</span>
                </div>
            </div>
            <div class="total">
                TOTAL PAYÉ : ${formattedAmount}
            </div>
           
            <div class="footer">
                <p>Méthode: ${paiement.moyenPaiement ||
                  'Non spécifiée'}</p>
                ${paiement.referenceTransaction ? `<p>Réf.
                  Trans: ${paiement.referenceTransaction}</p>` : ''}
                <p>Réceptionniste : ${paiement.caissierName ||
                  userRole.toUpperCase()}</p>
                <p style="margin-top: 10px;">Merci de votre confiance !</p>
            </div>
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
        printWindow.document.write(content);
        printWindow.document.close();
        
        const script = printWindow.document.createElement('script');
        script.innerHTML = `
            window.onload = function() {
                window.print();
            };
        `;
        printWindow.document.body.appendChild(script);

        setMessage('Le reçu a été préparé pour l\'impression. Veuillez autoriser l\'ouverture d\'une nouvelle fenêtre.');
    } else {
        setError("L'ouverture de la fenêtre d'impression a été bloquée par le navigateur. Veuillez autoriser les pop-ups pour ce site.");
    }
  };

  const handlePrintHistory = () => {
    if (!schoolData || !filteredPaiements || filteredPaiements.length === 0) {
        setError("Aucune donnée à imprimer ou informations de l'école manquantes. Veuillez charger les paiements et les détails de l'école.");
        return;
    }

    const schoolName = schoolData?.name || 'Nom de l\'École';
    const schoolAddress = schoolData?.adresse_ecole || 'Adresse de l\'École';
    const schoolPhone = schoolData?.telephone_ecole || 'Téléphone de l\'École';
    const schoolLogoHtml = schoolData?.logoUrl
        ? `<img src="${schoolData.logoUrl}" style="max-height: 60px; width: auto; margin: 0 auto 10px; display: block;" alt="Logo"/>`
        : `<h3 style="margin: 2px 0; font-size: 11pt;">${schoolName}</h3>`;

    let tableRowsHtml = '';
    filteredPaiements.forEach(paiement => {
        const eleve = eleves.find(e => e.id === paiement.eleveId);
        const soldeDuValue = eleve ? calculateSolde(eleve.id) : 0; 
        const eleveNomComplet = eleve?.name || 'Élève Inconnu';
        const classeName = paiement.className || 'N/A';
        const formattedMontant = formatCurrency(paiement.montantPaye);
        const formattedSoldeDu = typeof soldeDuValue === 'number' ? formatCurrency(soldeDuValue) : '';
        const formattedDate = paiement.datePaiement && paiement.datePaiement.toDate ?
            new Date(paiement.datePaiement.toDate()).toLocaleDateString('fr-FR') : 'N/A';
        const typeMois = paiement.typePaiement === 'mensualite' && paiement.moisConcerne ?
            `${paiement.typePaiement} (${paiement.moisConcerne})` : paiement.typePaiement;

        tableRowsHtml += `
            <tr>
                <td>${eleveNomComplet}</td>
                <td>${classeName}</td>
                <td class="text-right">${formattedMontant}</td>
                <td>${typeMois}</td>
                <td>${paiement.moyenPaiement}</td>
                <td class="text-right">${formattedSoldeDu}</td>
                <td>${formattedDate}</td>
            </tr>
        `;
    });

    const content = `
        <html>
        <head>
            <title>Historique des Paiements</title>
            <style>
                body {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    font-size: 10pt;
                    margin: 15mm;
                    color: #333;
                }
                .header {
                    text-align: center;
                    margin-bottom: 25px;
                    padding-bottom: 15px;
                    border-bottom: 1px solid #eee;
                }
                .header h1 {
                    margin: 8px 0;
                    font-size: 18pt;
                    color: #2c3e50;
                }
                .header h2 {
                    margin: 5px 0;
                    font-size: 14pt;
                    color: #34495e;
                }
                .header p {
                    margin: 3px 0;
                    font-size: 9pt;
                    color: #555;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 20px;
                }
                th, td {
                    border: 1px solid #ddd;
                    padding: 10px 8px;
                    text-align: left;
                    font-size: 9.5pt;
                }
                th {
                    background-color: #f8f8f8;
                    font-weight: bold;
                    color: #444;
                }
                .text-right {
                    text-align: right;
                }
                @media print {
                    body {
                        -webkit-print-color-adjust: exact;
                        color-adjust: exact;
                    }
                    th {
                        background-color: #f0f0f0 !important;
                    }
                }
            </style>
        </head>
        <body>
            <div class="header">
                ${schoolLogoHtml}
                <h1>${schoolName}</h1>
                <p>${schoolAddress}</p>
                <p>Tél: ${schoolPhone}</p>
                <h2>Historique des Paiements - Année Scolaire ${selectedSchoolYear}</h2>
                <p>Date d'impression : ${new Date().toLocaleDateString('fr-FR')}</p>
            </div>
            <table>
                <thead>
                    <tr>
                        <th>Élève</th>
                        <th>Classe</th>
                        <th class="text-right">Montant</th>
                        <th>Type/Mois</th>
                        <th>Moyen</th>
                        <th class="text-right">Solde Dû</th>
                        <th>Date</th>
                    </tr>
                </thead>
                <tbody>
                    ${tableRowsHtml}
                </tbody>
            </table>
        </body>
        </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.onload = () => {
            printWindow.print();
        };
        setMessage('L\'historique des paiements a été préparé pour l\'impression.');
    } else {
        setError("L'ouverture de la fenêtre d'impression a été bloquée par le navigateur. Veuillez autoriser les pop-ups pour ce site.");
    }
};

  const handlePrintStudentStatus = () => {
    if (!schoolData || !elevesStatusList || elevesStatusList.length === 0) {
      setError("Aucune donnée d'élève à imprimer ou informations de l'école manquantes.");
      return;
    }

    const schoolName = schoolData?.name || 'Nom de l\'École';
    const schoolAddress = schoolData?.adresse_ecole || 'Adresse de l\'École';
    const schoolPhone = schoolData?.telephone_ecole || 'Téléphone de l\'École';
    const schoolLogoHtml = schoolData?.logoUrl
      ? `<img src="${schoolData.logoUrl}" style="max-height: 60px; width: auto; margin: 0 auto 10px; display: block;" alt="Logo"/>`
      : `<h3 style="margin: 2px 0; font-size: 11pt;">${schoolName}</h3>`;

    let tableRowsHtml = '';
    elevesStatusList.forEach(status => {
      const studentClassInfo = getEleveClasseInfo(status.id);
      const className = studentClassInfo.classeNom || 'N/A';

      const monthlyStatusText = status.monthlyStatuses.map(m => {
        let statusAbbrev = '';
        if (m.status === 'PAYÉ') statusAbbrev = 'PAY';
        else if (m.status === 'PARTIEL') statusAbbrev = 'PAR';
        else if (m.status === 'IMPAYÉ') statusAbbrev = 'IMP';
        else statusAbbrev = 'N/A';
        return `${m.monthName.substring(0,3)}:${statusAbbrev}`;
      }).join(', ');

      tableRowsHtml += `
            <tr>
                <td>${status.name} (${className}) ${status.isCasSocial ? ' (CAS SOCIAL)' : ''}</td>
                <td>
                    <span style="display: inline-block; padding: 4px 8px; border-radius: 9999px; font-weight: 600; font-size: 0.8em; white-space: nowrap;
                        ${status.statusSummary === 'SOLDÉ' ? 'background-color: #d1fae5; color: #065f46;' :
                          status.statusSummary === 'À JOUR' ? 'background-color: #e0f2fe; color: #026aa2;' :
                          status.statusSummary === 'EN RETARD' ? 'background-color: #fee2e2; color: #991b1b;' :
                          'background-color: #f3f4f6; color: #4b5563;'
                        }"
                    >
                        ${status.statusSummary}
                    </span>
                </td>
                <td class="text-right">${formatCurrency(status.totalAnnualFee)}</td>
                <td class="text-right">${formatCurrency(status.totalPaid)}</td>
                <td class="text-right" style="color: ${status.soldeAnnuelDu > 0 ? '#991b1b' : '#065f46'}; font-weight: bold;">
                    ${formatCurrency(status.soldeAnnuelDu)}
                </td>
                <td class="text-right" style="color: ${status.soldeDuAjourdhui > 0 ? '#991b1b' : '#065f46'}; font-weight: bold;">
                    ${formatCurrency(status.soldeDuAjourdhui)}
                </td>
                <td><span style="font-size: 0.8em;">${monthlyStatusText}</span></td>
            </tr>
        `;
    });

    const content = `
        <html>
        <head>
            <title>Statut Financier des Élèves - ${selectedSchoolYear}</title>
            <style>
                body {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    font-size: 10pt;
                    margin: 15mm;
                    color: #333;
                }
                .header {
                    text-align: center;
                    margin-bottom: 25px;
                    padding-bottom: 15px;
                    border-bottom: 1px solid #eee;
                }
                .header h1 {
                    margin: 8px 0;
                    font-size: 18pt;
                    color: #2c3e50;
                }
                .header h2 {
                    margin: 5px 0;
                    font-size: 14pt;
                    color: #34495e;
                }
                .header p {
                    margin: 3px 0;
                    font-size: 9pt;
                    color: #555;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 20px;
                }
                th, td {
                    border: 1px solid #ddd;
                    padding: 8px 6px;
                    text-align: left;
                    font-size: 9pt;
                    vertical-align: top;
                }
                th {
                    background-color: #f8f8f8;
                    font-weight: bold;
                    color: #444;
                }
                .text-right {
                    text-align: right;
                }
                @media print {
                    body {
                        -webkit-print-color-adjust: exact;
                        color-adjust: exact;
                    }
                    th {
                        background-color: #f0f0f0 !important;
                    }
                }
            </style>
        </head>
        <body>
            <div class="header">
                ${schoolLogoHtml}
                <h1>${schoolName}</h1>
                <p>${schoolAddress}</p>
                <p>Tél: ${schoolPhone}</p>
                <h2>Statut Financier des Élèves - Année Scolaire ${selectedSchoolYear}</h2>
                <p>Date d'impression : ${new Date().toLocaleDateString('fr-FR')}</p>
                ${filterCasSocialOnly ? '<p>Filtre appliqué : Seulement les élèves "Cas Social"</p>' : ''}
            </div>
            <table>
                <thead>
                    <tr>
                        <th>Élève (Classe)</th>
                        <th>Statut Général</th>
                        <th class="text-right">Total Annuel Attendu</th>
                        <th class="text-right">Total Payé</th>
                        <th class="text-right">Solde Annuel Total</th>
                        <th class="text-right">Solde Dû Aujourd'hui</th>
                        <th>Détail Mensuel</th>
                    </tr>
                </thead>
                <tbody>
                    ${tableRowsHtml}
                </tbody>
            </table>
            <div style="margin-top: 30px; font-size: 9pt; color: #555;">
                <p><strong>Légende Statut Général:</strong></p>
                <ul>
                    <li><span style="background-color: #d1fae5; color: #065f46; padding: 2px 5px; border-radius: 4px; display: inline-block;">SOLDÉ</span>: Tous les frais annuels sont payés.</li>
                    <li><span style="background-color: #e0f2fe; color: #026aa2; padding: 2px 5px; border-radius: 4px; display: inline-block;">À JOUR</span>: Toutes les échéances passées ou présentes sont payées.</li>
                    <li><span style="background-color: #fee2e2; color: #991b1b; padding: 2px 5px; border-radius: 4px; display: inline-block;">EN RETARD</span>: Une ou plusieurs échéances passées/présentes ne sont pas entièrement payées.</li>
                    <li><span style="background-color: #f3f4f6; color: #4b5563; padding: 2px 5px; border-radius: 4px; display: inline-block;">AVANCE/RESTANT À PAYER</span>: Pas en retard, mais le total annuel n'est pas encore soldé.</li>
                </ul>
                <p style="margin-top: 15px;"><strong>Légende Détail Mensuel (Abrégé):</strong></p>
                <ul>
                    <li><span style="font-weight: bold;">PAY</span>: Le mois est entièrement payé.</li>
                    <li><span style="font-weight: bold;">PAR</span>: Le mois est partiellement payé.</li>
                    <li><span style="font-weight: bold;">IMP</span>: Le mois est impayé.</li>
                </ul>
            </div>
        </body>
        </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
      setMessage('Le rapport de statut financier a été préparé pour l\'impression.');
    } else {
      setError("L'ouverture de la fenêtre d'impression a été bloquée par le navigateur. Veuillez autoriser les pop-ups pour ce site.");
    }
  };

  // Fonctions CRUD et gestion des formulaires
  const handleSavePaiement = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    if (!selectedEleveId || !montantPaye || !typePaiement || !moyenPaiement || (typePaiement === 'mensualite' && !moisConcerne)) {
      setError("Veuillez remplir tous les champs obligatoires du paiement.");
      return;
    }
    const parsedMontant = parseFloat(montantPaye);
    if (isNaN(parsedMontant) || parsedMontant <= 0) {
      setError("Le montant payé doit être un nombre positif.");
      return;
    }

    const selectedEleve = eleves.find(e => e.id === selectedEleveId);
    if (!selectedEleve) {
      setError("Erreur: Impossible de trouver les données de l'élève sélectionné. Assurez-vous qu'il est inscrit.");
      return;
    }

    const dataToSaveCore = {
      eleveId: selectedEleveId,
      montant: parsedMontant,
      montantPaye: parsedMontant,
      moisConcerne: typePaiement === 'mensualite' ?
        moisConcerne : '',
      typePaiement: typePaiement,
      description: description,
      moyenPaiement: moyenPaiement,
      methodePaiement: moyenPaiement,
      referenceTransaction: moyenPaiement !== 'Espèces' ?
        referenceTransaction : '',
      datePaiement: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    try {
    let eleveNameForReceivable = selectedEleve.displayName || selectedEleve.name;
    let finalEleveId = selectedEleveId;

    if (isEditing) {
      const paiementRef = doc(db, 'schools', schoolId, 'paiements', currentPaiementId);
      await updateDoc(paiementRef, dataToSaveCore);
      setMessage("Paiement mis à jour avec succès !");
      const updatedSolde = calculateSolde(finalEleveId); 
      await updateStudentReceivable(finalEleveId, schoolId, updatedSolde, eleveNameForReceivable);
   
      } else {
        const agentId = auth?.currentUser?.uid ||
          'N/A';
        const dataToSaveNew = {
          ...dataToSaveCore,
          eleveName: selectedEleve.displayName ||
            selectedEleve.name, 
          classeId: selectedEleve.classeId || null,
          className: selectedEleve.className ||
            'N/A', 
          schoolYear: selectedEleve.schoolYear || 'N/A', 
          schoolId: schoolId,
          agentId: agentId, 
          caissierName: auth?.currentUser?.displayName || 'Caissier/ère', 
          createdAt: serverTimestamp(), 
        };
        const docRef = await addDoc(collection(db, 'schools', schoolId, 'paiements'), dataToSaveNew);
      setMessage("Paiement enregistré avec succès !");
      handlePrintPaymentReceipt({ ...dataToSaveNew, id: docRef.id }, docRef.id);
      const updatedSolde = calculateSolde(finalEleveId); 
      await updateStudentReceivable(finalEleveId, schoolId, updatedSolde, eleveNameForReceivable);
    }
    resetForm();
  } catch (error) {
    setError(`Échec de l'enregistrement du paiement: ${error.message}`);
  }
};

  const handleDelete = async (id) => {
  if (window.confirm("Êtes-vous sûr de vouloir supprimer ce paiement ?")) {
    try {
      const paiementToDeleteRef = doc(db, 'schools', schoolId, 'paiements', id);
      const paiementSnap = await getDoc(paiementToDeleteRef);
      if (!paiementSnap.exists()) {
          setError("Paiement introuvable pour suppression.");
          return;
      }
      const eleveIdAffected = paiementSnap.data().eleveId;
      const eleveNameAffected = paiementSnap.data().eleveName;

      await deleteDoc(paiementToDeleteRef);
      setMessage("Paiement supprimé avec succès !");

      setTimeout(async () => {
        const updatedSolde = calculateSolde(eleveIdAffected); 
        await updateStudentReceivable(eleveIdAffected, schoolId, updatedSolde, eleveNameAffected);
      }, 500);
      
    } catch (error) {
      setError(`Échec de la suppression du paiement: ${error.message}`);
    }
  }
};

  const handleEdit = (paiement) => {
    setSelectedEleveId(paiement.eleveId);
    setMontantPaye(paiement.montantPaye?.toString() || '');
    setMoisConcerne(paiement.moisConcerne || '');
    setTypePaiement(paiement.typePaiement || 'mensualite');
    setDescription(paiement.description || '');
    setMoyenPaiement(paiement.moyenPaiement || '');
    setReferenceTransaction(paiement.referenceTransaction || '');
    setIsEditing(true);
    setCurrentPaiementId(paiement.id);
  };
  
  const resetForm = () => {
    setIsEditing(false);
    setCurrentPaiementId(null);
    setSelectedEleveId('');
    setMontantPaye('');
    setMoisConcerne('');
    setTypePaiement('mensualite');
    setDescription('');
    setMoyenPaiement('');
    setReferenceTransaction('');
  };
  
  const handleSelectedEleveChange = (e) => {
    const eleveId = e.target.value;
    setSelectedEleveId(eleveId);
    const selectedEleve = eleves.find(e => e.id === eleveId);
    
    if (eleveId && typePaiement) {
      const { classeInfo } = getEleveClasseInfo(eleveId);
      
      if (selectedEleve?.isCasSocial) {
          setMontantPaye('');
          return;
      }

      if (classeInfo) {
        if (typePaiement === 'inscription') {
          setMontantPaye(classeInfo.montantInscription ? classeInfo.montantInscription.toString() : '');
        } else if (typePaiement === 'mensualite') {
          setMontantPaye(classeInfo.montantMensualite ? classeInfo.montantMensualite.toString() : '');
        } else {
          setMontantPaye('');
        }
      } else {
        setMontantPaye('');
      }
    } else {
      setMontantPaye('');
    }
  };

  const handleTypePaiementChange = (e) => {
    const newType = e.target.value;
    setTypePaiement(newType);
    if (selectedEleveId) {
      const selectedEleve = eleves.find(e => e.id === selectedEleveId);
      const { classeInfo } = getEleveClasseInfo(selectedEleveId);
      
      if (selectedEleve?.isCasSocial) {
          setMontantPaye('');
          if (newType !== 'mensualite') { setMoisConcerne(''); }
          return;
      }
      
      if (classeInfo) {
        if (newType === 'inscription') {
          setMontantPaye(classeInfo.montantInscription ? classeInfo.montantInscription.toString() : '');
        } else if (newType === 'mensualite') {
          setMontantPaye(classeInfo.montantMensualite ? classeInfo.montantMensualite.toString() : '');
        } else {
          setMontantPaye('');
        }
      } else {
        setMontantPaye('');
      }
    } else {
      setMontantPaye('');
    }
    if (newType !== 'mensualite') { setMoisConcerne(''); }
  };
  
  const paymentMethods = ['Orange Money', 'Wave', 'Carte bancaire', 'Espèces'];

  // --- RENDU ---
  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 p-4">
        <Loader2 className="animate-spin text-blue-500" size={48} />
        <p className="ml-3 text-lg text-gray-700 mt-4">Chargement des données de paiement...</p>
      </div>
    );
  }
  
  if (userRole === 'superAdmin' && !schoolId) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg shadow-md">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 flex items-center">
          <DollarSign size={24} className="mr-3 text-blue-600" /> Gestion des Paiements
        </h2>
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 mb-4">
          <p className="font-semibold">Veuillez sélectionner une école dans le tableau de bord pour gérer les paiements.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8">
      
      {/* --- Affichage des KPI et totaux de caisse --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        
        {/* KPI 1: Taux de Recouvrement (À Jour) */}
        <div className="bg-white p-4 sm:p-5 rounded-lg shadow-md border-t-4 border-red-500">
          <p className="text-xs sm:text-sm font-medium text-gray-500">Taux Recouvrement À Jour</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {recoveryStats.dueDateRecoveryRate.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500 mt-1 truncate">
              {formatCurrency(recoveryStats.totalPaid)} / {formatCurrency(recoveryStats.totalExpectedDueToDate)}
          </p>
        </div>
        
        {/* KPI 2: Taux de Recouvrement (Annuel Total) */}
        <div className="bg-white p-4 sm:p-5 rounded-lg shadow-md border-t-4 border-blue-500">
          <p className="text-xs sm:text-sm font-medium text-gray-500">Taux Recouvrement Annuel</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {recoveryStats.annualRecoveryRate.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500 mt-1 truncate">
              {formatCurrency(recoveryStats.totalPaid)} / {formatCurrency(recoveryStats.totalAnnualExpected)}
          </p>
        </div>

        {/* Caisse 1: Encaissement Espèces Aujourd'hui */}
        <div className="bg-white p-4 sm:p-5 rounded-lg shadow-md border-t-4 border-indigo-500">
          <p className="text-xs sm:text-sm font-medium text-gray-500">Espèces Aujourd'hui</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(totalCashInToday)}</p>
          <p className="text-xs text-gray-500 mt-1">Mois: {formatCurrency(totalCashInMonth)}</p>
        </div>
        
        {/* Caisse 2: Total Payé */}
        <div className="bg-white p-4 sm:p-5 rounded-lg shadow-md border-t-4 border-green-500">
          <p className="text-xs sm:text-sm font-medium text-gray-500">Total Payé (Toutes Méthodes)</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(recoveryStats.totalPaid)}</p>
          <p className="text-xs text-gray-500 mt-1 truncate">École: {schoolData?.name || 'N/A'}</p>
        </div>
      </div>

      {/* Sélecteur d'année scolaire mobile-friendly */}
      <div className="mb-4">
        <label htmlFor="schoolYearSelect" className="block text-sm font-medium text-gray-700 mb-2">
          Année Scolaire
        </label>
        <select
          id="schoolYearSelect"
          value={selectedSchoolYear}
          onChange={(e) => setSelectedSchoolYear(e.target.value)}
          className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
        >
          {recentSchoolYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* TABS (ONGLETS) - Version mobile améliorée */}
      <div className="mb-6">
        {/* Menu mobile pour les tabs */}
        <div className="sm:hidden mb-4">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="w-full flex justify-between items-center px-4 py-3 bg-gray-100 rounded-lg"
          >
            <span className="font-medium">
              {activeView === 'paiementsHistory' ? 'Historique des Paiements' : 'Statut Financier des Élèves'}
            </span>
            <ChevronDown className={`transform ${isMobileMenuOpen ? 'rotate-180' : ''} transition-transform`} />
          </button>
          
          {isMobileMenuOpen && (
            <div className="mt-2 bg-white border rounded-lg shadow-lg">
              <button
                onClick={() => {
                  setActiveView('paiementsHistory');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 ${activeView === 'paiementsHistory' ? 'bg-indigo-50 text-indigo-600' : 'hover:bg-gray-50'}`}
              >
                Historique des Paiements
              </button>
              <button
                onClick={() => {
                  setActiveView('studentStatus');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 ${activeView === 'studentStatus' ? 'bg-indigo-50 text-indigo-600' : 'hover:bg-gray-50'}`}
              >
                Statut Financier des Élèves
              </button>
            </div>
          )}
        </div>

        {/* Tabs desktop */}
        <div className="hidden sm:block border-b border-gray-200">
          <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
            <li className="mr-2">
              <button
                onClick={() => setActiveView('paiementsHistory')}
                className={`inline-block p-4 border-b-2 rounded-t-lg ${activeView === 'paiementsHistory' ? 'text-indigo-600 border-indigo-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}
                type="button"
              >
                Historique des Paiements
              </button>
            </li>
            <li className="mr-2">
              <button
                onClick={() => setActiveView('studentStatus')}
                className={`inline-block p-4 border-b-2 rounded-t-lg ${activeView === 'studentStatus' ? 'text-indigo-600 border-indigo-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}
                type="button"
              >
                Statut Financier des Élèves
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* --- VUE 1: FORMULAIRE ET HISTORIQUE DES PAIEMENTS --- */}
      {activeView === 'paiementsHistory' && (
        <>
          <div className="bg-white shadow-xl rounded-xl p-4 sm:p-6 mb-6">
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6 border-b pb-3">
              {isEditing ? 'Modifier un Paiement' : 'Enregistrer un Nouveau Paiement'}
            </h3>
            
            <form onSubmit={handleSavePaiement} className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              
              {/* 1. Élève */}
              <div className="sm:col-span-1">
                <label htmlFor="eleveId" className="block text-sm font-medium text-gray-700">Élève</label>
                <select
                  id="eleveId"
                  value={selectedEleveId}
                  onChange={handleSelectedEleveChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  required
                  disabled={isEditing}
                >
                  <option value="">Sélectionnez un élève</option>
                  {eleves
                    .filter(eleve => eleve.schoolYear === selectedSchoolYear)
                    .map(eleve => (
                      <option key={eleve.id} value={eleve.id}>
                        {eleve.name} ({eleve.className || 'N/A'})
                        {eleve.isCasSocial ? ` - CAS SOCIAL` : ''} 
                      </option>
                    ))}
                </select>
              </div>

              {/* 2. Type de Paiement */}
              <div className="sm:col-span-1">
                <label htmlFor="typePaiement" className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  id="typePaiement"
                  value={typePaiement}
                  onChange={handleTypePaiementChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  required
                >
                  <option value="inscription">Frais d'Inscription</option>
                  <option value="mensualite">Mensualité</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              {/* 3. Montant Payé */}
              <div className="sm:col-span-1">
                <label htmlFor="montantPaye" className="block text-sm font-medium text-gray-700">Montant (XOF)</label>
                <input
                  type="number"
                  id="montantPaye"
                  value={montantPaye}
                  onChange={(e) => setMontantPaye(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  placeholder="Ex: 50000"
                  required
                />
                {selectedEleveId && eleves.find(e => e.id === selectedEleveId)?.isCasSocial && (
                  <p className="mt-1 text-xs text-orange-600 font-medium">
                    Cas Social : Saisir manuellement
                  </p>
                )}
              </div>

              {/* 4. Mois Concerné (Conditionnel) */}
              {typePaiement === 'mensualite' && (
                <div className="sm:col-span-1">
                  <label htmlFor="moisConcerne" className="block text-sm font-medium text-gray-700">Mois/Année</label>
                  <input
                    type="month"
                    id="moisConcerne"
                    value={moisConcerne}
                    onChange={(e) => setMoisConcerne(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm"
                    required
                  />
                </div>
              )}
              
              {/* 5. Moyen de Paiement */}
              <div className="sm:col-span-1">
                <label htmlFor="moyenPaiement" className="block text-sm font-medium text-gray-700">Moyen de Paiement</label>
                <select
                  id="moyenPaiement"
                  value={moyenPaiement}
                  onChange={(e) => setMoyenPaiement(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  required
                >
                  <option value="">Sélectionnez</option>
                  {paymentMethods.map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
              
              {/* 6. Référence de Transaction (Conditionnel) */}
              {(moyenPaiement && moyenPaiement !== 'Espèces') && (
                <div className="sm:col-span-1">
                  <label htmlFor="referenceTransaction" className="block text-sm font-medium text-gray-700">Référence</label>
                  <input
                    type="text"
                    id="referenceTransaction"
                    value={referenceTransaction}
                    onChange={(e) => setReferenceTransaction(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm"
                    placeholder="Réf. transaction"
                    required={moyenPaiement !== 'Espèces'}
                  />
                </div>
              )}

              {/* 7. Description */}
              <div className="sm:col-span-full">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description (Optionnel)</label>
                <textarea
                  id="description"
                  rows="2"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm"
                  placeholder="Détails supplémentaires"
                />
              </div>

              {/* Boutons d'action */}
              <div className="sm:col-span-full flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150"
                >
                  {isEditing ? 'Sauvegarder' : 'Enregistrer le Paiement'}
                </button>
              </div>
            </form>
          </div>
          
          <div className="bg-white shadow-xl rounded-xl p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-0">
                Historique des Paiements
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowFilter(!showFilter)}
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-md hover:bg-gray-100"
                  title="Filtrer"
                >
                  <Filter size={20} className={showFilter ? 'text-indigo-600' : ''} />
                </button>
                <button
                  onClick={handlePrintHistory}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 text-sm flex items-center transition duration-150"
                  title="Imprimer"
                >
                  <Printer size={16} className="mr-2" /> <span className="hidden sm:inline">Imprimer</span>
                </button>
              </div>
            </div>
            
            {/* Filtres */}
            {showFilter && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6 p-4 border rounded-lg bg-gray-50">
                <div>
                  <label htmlFor="filterEleveId" className="block text-xs sm:text-sm font-medium text-gray-700">Élève</label>
                  <select
                    id="filterEleveId"
                    value={filterEleveId}
                    onChange={(e) => setFilterEleveId(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-xs sm:text-sm"
                  >
                    <option value="">Tous</option>
                    {eleves
                      .filter(eleve => eleve.schoolYear === selectedSchoolYear)
                      .map(eleve => (
                        <option key={eleve.id} value={eleve.id}>{eleve.name}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="filterClasseId" className="block text-xs sm:text-sm font-medium text-gray-700">Classe</label>
                  <select
                    id="filterClasseId"
                    value={filterClasseId}
                    onChange={(e) => setFilterClasseId(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-xs sm:text-sm"
                  >
                    <option value="">Toutes</option>
                    {classes.map(classe => (
                      <option key={classe.id} value={classe.id}>{classe.nom}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="filterTypePaiement" className="block text-xs sm:text-sm font-medium text-gray-700">Type</label>
                  <select
                    id="filterTypePaiement"
                    value={filterTypePaiement}
                    onChange={(e) => setFilterTypePaiement(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-xs sm:text-sm"
                  >
                    <option value="">Tous</option>
                    <option value="inscription">Inscription</option>
                    <option value="mensualite">Mensualité</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div className="flex items-center pt-5">
                  <input
                    id="filterSoldeNonAJour"
                    name="filterSoldeNonAJour"
                    type="checkbox"
                    checked={filterSoldeNonAJour}
                    onChange={(e) => setFilterSoldeNonAJour(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="filterSoldeNonAJour" className="ml-2 block text-xs sm:text-sm font-medium text-gray-700">
                    Solde Dû &gt; 0
                  </label>
                </div>
              </div>
            )}

            {/* Tableau des Paiements - Version responsive */}
            <ResponsiveTable>
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-6">
                      Élève
                    </th>
                    <th scope="col" className="hidden sm:table-cell px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      Classe
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      Montant
                    </th>
                    <th scope="col" className="hidden lg:table-cell px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      Type/Mois
                    </th>
                    <th scope="col" className="hidden md:table-cell px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      Moyen
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      Solde
                    </th>
                    <th scope="col" className="hidden sm:table-cell px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      Date
                    </th>
                    {(userRole === 'financier' || userRole === 'admin' || userRole === 'superAdmin') && (
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredPaiements.map(paiement => {
                    const eleve = eleves.find(e => e.id === paiement.eleveId);
                    const soldeDuValue = eleve ? calculateSolde(eleve.id) : 0; 
                    const { eleveNomComplet, telephoneParent } = getEleveClasseInfo(paiement.eleveId);

                    const soldeDuNumeric = typeof soldeDuValue === 'number' ? soldeDuValue : 0;
                    const isSoldeNonAJour = soldeDuNumeric > 0;
                    
                    const whatsAppLink = isSoldeNonAJour && eleve 
                      ? generateWhatsAppLink(eleveNomComplet, soldeDuNumeric, telephoneParent) 
                      : null;

                    return (
                      <tr key={paiement.id} className={isSoldeNonAJour ? 'bg-red-50' : ''}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900">{eleveNomComplet}</div>
                          <div className="text-gray-500 text-xs">{paiement.schoolYear}</div>
                          {eleve?.isCasSocial && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-xs font-bold bg-orange-100 text-orange-800 rounded-full">
                              CAS SOCIAL
                            </span>
                          )}
                          <div className="sm:hidden text-xs text-gray-500 mt-1">
                            {paiement.className || 'N/A'} • {paiement.moyenPaiement}
                          </div>
                        </td>
                        <td className="hidden sm:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {paiement.className || 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-bold text-green-600">
                          {formatCurrency(paiement.montantPaye)}
                        </td>
                        <td className="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {paiement.typePaiement === 'mensualite' && paiement.moisConcerne 
                            ? `Mensualité (${paiement.moisConcerne})`
                            : paiement.typePaiement}
                        </td>
                        <td className="hidden md:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {paiement.moyenPaiement}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <div className="flex items-center">
                            <span className={`font-bold ${soldeDuNumeric > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {typeof soldeDuValue === 'number' ? formatCurrency(soldeDuValue) : soldeDuValue}
                            </span>
                            {isSoldeNonAJour && whatsAppLink && (
                              <a href={whatsAppLink} target="_blank" rel="noopener noreferrer" 
                                 className="ml-2 text-green-500 hover:text-green-700" 
                                 title="Envoyer rappel WhatsApp">
                                <MessageSquare size={16} />
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="hidden sm:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {paiement.datePaiement && paiement.datePaiement.toDate
                            ? new Date(paiement.datePaiement.toDate()).toLocaleDateString('fr-FR')
                            : 'N/A'}
                        </td>
                        {(userRole === 'financier' || userRole === 'admin' || userRole === 'superAdmin') && (
                          <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex flex-col sm:flex-row sm:space-x-3 space-y-1 sm:space-y-0">
                              <button
                                onClick={() => handlePrintPaymentReceipt(paiement)}
                                className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm"
                              >
                                Imprimer
                              </button>
                              <button
                                onClick={() => handleEdit(paiement)}
                                className="text-indigo-600 hover:text-indigo-900 text-xs sm:text-sm"
                              >
                                Éditer
                              </button>
                              <button
                                onClick={() => handleDelete(paiement.id)}
                                className="text-red-600 hover:text-red-900 text-xs sm:text-sm"
                              >
                                Supprimer
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ResponsiveTable>
          </div>
        </>
      )}

      {/* --- VUE 2: STATUT FINANCIER DES ÉLÈVES --- */}
      {activeView === 'studentStatus' && (
        <div className="bg-white shadow-xl rounded-xl p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-0">
              Statut Financier des Élèves ({selectedSchoolYear})
            </h3>
            
            <div className="flex flex-wrap items-center gap-3">
              {/* Filtre Cas Social */}
              <div className="flex items-center">
                <label htmlFor="filterCasSocialOnly" className="block text-xs sm:text-sm font-medium text-gray-700 mr-2">
                  Cas Social
                </label>
                <input
                  id="filterCasSocialOnly"
                  name="filterCasSocialOnly"
                  type="checkbox"
                  checked={filterCasSocialOnly}
                  onChange={(e) => setFilterCasSocialOnly(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
              </div>

              {/* Bouton Rappels Groupés */}
              <button
                onClick={() => setShowBulkReminderPanel(!showBulkReminderPanel)}
                className={`px-3 py-2 text-sm font-medium rounded-md text-white transition duration-150 flex items-center ${
                  overdueStudents.length > 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-400 cursor-not-allowed'
                }`}
                disabled={overdueStudents.length === 0}
              >
                <MessageSquare size={16} className="mr-2" />
                <span className="hidden sm:inline">Rappels Groupés</span>
                <span className="sm:ml-1">({overdueStudents.length})</span>
              </button>
              
              {/* Bouton Imprimer */}
              <button
                onClick={handlePrintStudentStatus}
                className="px-3 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 text-sm flex items-center transition duration-150"
              >
                <Printer size={16} className="mr-2" /> 
                <span className="hidden sm:inline">Imprimer</span>
              </button>
            </div>
          </div>
          
          {/* Panneau de Rappel Groupé */}
          {showBulkReminderPanel && (
            <div className="mt-4 p-4 border border-red-300 rounded-lg bg-red-50">
              <h4 className="text-lg font-semibold text-red-800 mb-3">
                {overdueStudents.length} Élève(s) en Retard
              </h4>
              {overdueStudents.length === 0 ? (
                <p className="text-red-700">Aucun élève en retard.</p>
              ) : (
                <>
                  <p className="text-sm text-red-700 mb-3">
                    Cliquez sur chaque lien pour envoyer le rappel WhatsApp.
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                    {overdueStudents.map((student) => (
                      <div key={student.id} className="p-3 border rounded-md bg-white flex flex-col sm:flex-row sm:items-center justify-between text-sm">
                        <div className="mb-2 sm:mb-0">
                          <span className="font-medium block">{student.name}</span>
                          <span className="text-red-600">{formatCurrency(student.soldeDu)} dû</span>
                        </div>
                        <a 
                          href={student.whatsAppLink} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-green-600 hover:text-green-800 font-semibold flex items-center"
                        >
                          <MessageSquare size={16} className="mr-1" /> Envoyer WA
                        </a>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <button 
                onClick={() => setShowBulkReminderPanel(false)}
                className="mt-4 px-3 py-1 text-sm bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          )}

          {/* Tableau Statut Financier - Version responsive */}
          <ResponsiveTable className="mt-6">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-6">
                    Élève
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Statut
                  </th>
                  <th scope="col" className="hidden lg:table-cell px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Total Attendu
                  </th>
                  <th scope="col" className="hidden md:table-cell px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Payé
                  </th>
                  <th scope="col" className="hidden lg:table-cell px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Solde Annuel
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Dû Auj.
                  </th>
                  <th scope="col" className="hidden sm:table-cell px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Mensuel
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {elevesStatusList.map(status => (
                  <tr key={status.id} className={status.soldeDuAjourdhui > 0 ? 'bg-red-50' : ''}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                      <div className="font-medium text-gray-900">{status.name}</div>
                      <div className="text-gray-500 text-xs">{status.className || 'N/A'}</div>
                      {status.isCasSocial && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-bold bg-orange-100 text-orange-800 rounded-full">
                          CAS SOCIAL
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        status.statusSummary === 'SOLDÉ' ? 'bg-green-100 text-green-800' :
                        status.statusSummary === 'À JOUR' ? 'bg-blue-100 text-blue-800' :
                        status.statusSummary === 'EN RETARD' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {status.statusSummary}
                      </span>
                    </td>
                    <td className="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {formatCurrency(status.totalAnnualFee)}
                    </td>
                    <td className="hidden md:table-cell whitespace-nowrap px-3 py-4 text-sm text-green-600 font-bold">
                      {formatCurrency(status.totalPaid)}
                    </td>
                    <td className="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm font-bold">
                      <span className={status.soldeAnnuelDu > 0 ? 'text-red-700' : 'text-green-700'}>
                        {formatCurrency(status.soldeAnnuelDu)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-bold">
                      <div className="flex items-center">
                        <span className={status.soldeDuAjourdhui > 0 ? 'text-red-700' : 'text-green-700'}>
                          {formatCurrency(status.soldeDuAjourdhui)}
                        </span>
                        {status.soldeDuAjourdhui > 0 && status.telephoneParent && (
                          <a href={generateWhatsAppLink(status.name, status.soldeDuAjourdhui, status.telephoneParent)} 
                             target="_blank" 
                             rel="noopener noreferrer" 
                             className="ml-2 text-green-500 hover:text-green-700" 
                             title="Rappel WhatsApp">
                            <MessageSquare size={16} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-3 py-4 text-sm text-gray-500">
                      <div className="flex space-x-1">
                        {status.monthlyStatuses.slice(0, 6).map((m, index) => (
                          <span key={index} title={`${m.monthName}: ${m.status}`}
                            className={`w-2.5 h-2.5 rounded-full inline-block ${
                              m.status === 'PAYÉ' ? 'bg-green-500' :
                              m.status === 'PARTIEL' ? 'bg-yellow-500' :
                              m.status === 'IMPAYÉ' ? 'bg-red-500' :
                              'bg-gray-300'
                            }`}
                          ></span>
                        ))}
                        {status.monthlyStatuses.length > 6 && (
                          <span className="text-xs text-gray-400">+{status.monthlyStatuses.length - 6}</span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      {status.soldeDuAjourdhui > 0 && status.telephoneParent && (
                        <a href={generateWhatsAppLink(status.name, status.soldeDuAjourdhui, status.telephoneParent)} 
                           target="_blank" 
                           rel="noopener noreferrer" 
                           className="text-green-500 hover:text-green-700" 
                           title="Rappel WhatsApp">
                          <MessageSquare size={18} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResponsiveTable>
        </div>
      )}
    </div>
  );
};

export default PaiementsManagement;